import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { CUSTOM_ELEMENTS_SCHEMA, DoBootstrap, Injector, NgModule, inject, provideAppInitializer } from '@angular/core';
import { createCustomElement } from '@angular/elements';

// Modules
import { BrowserModule } from '@angular/platform-browser';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CoreModule } from '@core/core.module';
import { PrimeNgModule } from '@shared/primeng/prime-ng.module';
import { DirectiveModule } from '@shared/directive/directive.module';
import {
  FeatureToggleModule,
  SecurityModule,
  SecurityService,
} from '@mynexus/mynexus-ui-lib';

// Services
import { AuthenticationService, ConfigurationService } from '@core/service';
import { ActivityCenterMfeService } from './core/service/activitycenter-mfe.service';

// Components
import { MncActivityCenterComponent } from './mnc-activitycenter.component';
import { EmptyComponent } from '@shared/empty/empty.component';

import { environment } from '@env/environment';
import { PrimeNGConfigLoader, providePrimeNGFromShell, RouteInterceptor, ShellService, TokenInterceptor } from '@mynexus/mfe-core';
import { HttpErrorHandler, HttpUtility } from './core/http';
import { ConfirmationService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';

function loadConfig(authenticationService: AuthenticationService, securityService: SecurityService) {
  return async () => {
    const permissions = authenticationService.userPermissions;
    securityService.setUserPermissions(permissions.flat());

    // Legacy permissions model force us to set this false to avoid problem with empty permissions users
    securityService.setAllowEmptyPermission(false);
  }
}

/**
 * MFE-specific default theme loader. Lazy-loads `@mynexus/theme` so the Carelon
 * preset is only fetched when the shell hasn't supplied its own theme.
 */
const loadCarelonPrimeNGConfig: PrimeNGConfigLoader = async () => {
  const { Carelon, CSS_LAYERS_ORDER } = await import('@mynexus/theme');

  return {
    ripple: false,
    theme: {
      preset: Carelon,
      options: {
        darkModeSelector: false,
        cssLayer: {
          name: 'primeng',
          order: CSS_LAYERS_ORDER
        }
      },
    }
  };
};

@NgModule({
  declarations: [
    MncActivityCenterComponent,
    EmptyComponent,
  ],
  imports: [
    BrowserModule,
    FormsModule,
    PrimeNgModule,
    CoreModule,
    ReactiveFormsModule,
    DirectiveModule,
    FeatureToggleModule,
    // Uncomment if the application needs routing.
    // RouterModule.forRoot([
    //   ...environment.routes,
    //   // Required. Do not remove
    //   { path: '**', component: EmptyComponent },
    // ]),
    FeatureToggleModule.forRoot(environment.apiUrl),
    SecurityModule.forRoot([], environment.apiUrl),
  ],
  exports: [
    PrimeNgModule,
    DirectiveModule,
    ...environment.modules,
  ],
  providers: [
    ConfigurationService,
    HttpUtility,
    HttpErrorHandler,
    ConfirmationService,
    DialogService,
    provideAppInitializer(() => {
      const initializerFn = (loadConfig)(inject(AuthenticationService), inject(SecurityService));
      return initializerFn();
    }),
    providePrimeNGFromShell(loadCarelonPrimeNGConfig),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RouteInterceptor,
      multi: true
    },
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class MncActivityCenterModule implements DoBootstrap {
  constructor(
    private injector: Injector,
    private shellService: ShellService,
    private mfeService: ActivityCenterMfeService
  ) { }

  ngDoBootstrap(): void {
    this.registerCustomElement('mnc-activitycenter', MncActivityCenterComponent);

    this.shellService.registerMfeService('mnc-activitycenter', this.mfeService);
  }

  private registerCustomElement(name: string, component: any) {
    if (customElements.get(name)) return;
    customElements.define(
      name,
      createCustomElement<any>(component, { injector: this.injector }));
  }
}
